import { useState, useEffect } from 'react';
import axios from 'axios';
import useLogger from 'hooks/useLogger';
import { useAppSelector } from 'store/store';
import {
  apiEndpointsSelector,
  storeIdentifierNameSelector,
  storeIdSelector,
} from 'store/selectors/commonSelector';
import {
  clearanceSelector,
  previewSelector,
  skuCategorySelector,
} from 'store/selectors/quickViewSelector';
import { envData } from '../../../environments';
import { micrositeStores } from '../utility/utils';

type UseQuickviewProductDetailsProps = {
  selectedStoreId: number;
};

const PRODUCT_DETAILS_URL = 'v4/byPartNumber';
const getPrefix = (currentStore: string, host: string) => {
  if (host.includes('https://')) {
    return host;
  }
  // Uncomment the below while running locally. Commented because of failing tests.
  // if (window.location.href.indexOf('localhost') > -1) {
  //   return `https://api-search.${envData[currentStore].qaDomainName}${host}`;
  // }
  return `https://api-search.${envData[currentStore].domainName}${host}`;
};

const replaceQAWithLoadEndpoint = (requestURL: string) => {
  // When the host domain is static-search.dicksportinggoods.com hit load instead for testing reasons
  if (
    requestURL.includes('/qa/catalog-productdetails/') &&
    (window.location.host.includes('static-search.dickssportinggoods.com') ||
      window.location.host.includes('localhost'))
  ) {
    return requestURL.replace('/qa/catalog-productdetails/', '/load/catalog-productdetails/');
  }
  return requestURL;
};

const useQuickviewProductDetails = ({ selectedStoreId }: UseQuickviewProductDetailsProps) => {
  const { warn } = useLogger();
  // product details
  const [productDetails, setProductDetails] = useState({});
  // loading state
  const [loading, setLoading] = useState(true);
  // error state
  const [error, setError] = useState({});
  const storeId = useAppSelector(storeIdSelector);
  const apiEndpoints = useAppSelector(apiEndpointsSelector);
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const clearance = useAppSelector(clearanceSelector);
  const skuCategory = useAppSelector(skuCategorySelector);
  const preview = useAppSelector(previewSelector);

  const [ecode, setEcode] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Request product details from the quickview product details api
        const params = {
          id: ecode,
          inventory: true,
          selectedStore: selectedStoreId ?? '0',
          ...(clearance ? { clearance: true } : { clearance: false }),
          ...(micrositeStores?.find((store) => store === storeIdentifierName) && {
            channel: storeIdentifierName,
          }),
          ...(skuCategory && { categoryId: skuCategory }),
        };

        let requestURL = `${
          apiEndpoints.productDetailsEndpoint.startsWith('https')
            ? apiEndpoints.productDetailsEndpoint
            : getPrefix(storeIdentifierName, apiEndpoints.productDetailsEndpoint)
        }${PRODUCT_DETAILS_URL}/${storeId}`;
        requestURL = replaceQAWithLoadEndpoint(requestURL);

        const response = await axios.get(requestURL, {
          params,
          headers: {
            'Content-Type': 'application/json',
            preview: `${preview}`,
          },
          withCredentials: true,
        });

        if (response.status === 200 && response?.data?.productsData) {
          setProductDetails(response.data.productsData[0]);
          setLoading(false);
        } else {
          // If there is any non-200 status code let's throw an error
          throw new Error('Non-200 Response From Quickview Product Details API');
        }
      } catch (e) {
        warn('useQuickviewProductDetails - ', e);
        setError(true);
        setLoading(false);
      }
    };

    if (ecode) {
      setLoading(true);
      setError(false);
      fetchData();
    }
  }, [ecode]);

  return [productDetails, loading, error, ecode, setEcode];
};

export default useQuickviewProductDetails;
